/**
 * Derives the "Needs Your Attention" model for a TAS application's course-management
 * (`manage`) screen — a pure function over the application's data so it can be unit
 * tested without rendering. Mirrors the 9 distinct states in the Figma "Application
 * Review" concept (collapsed/expanded duplicates collapse to these 9).
 *
 * Task vs anchor:
 * - TASKS are actionable (Use CourseCash, Upload receipt, Submit evidence): they carry
 *   a due/use-by chip + CTA and count toward the "(N tasks pending)" header + right-rail
 *   Tasks list. A task can be `completed` (shown in the rail, NOT as an NYA card).
 * - ANCHORS are informational (submitted, approved, course-cash-used, all-caught-up):
 *   no chip/CTA, never counted.
 *
 * The panel has three zero-task shapes: the submitted anchor, the all-caught-up empty
 * state, and "hidden entirely" (`visible: false`).
 *
 * TODO(PR#2 wiring): confirm the live sources for `courseCashUseByDate`,
 * `evidenceDueDate`, `receiptDueDate`, and `receiptUploaded` — they are placeholders
 * ([mm/dd]) in the design and have no confirmed model field yet. The derivation is
 * shaped to accept them so wiring is a data-plumbing change, not a logic change.
 */

export type AttentionTaskKind = 'use-course-cash' | 'upload-receipt' | 'submit-evidence';

export type AttentionAnchorKind = 'submitted' | 'approved' | 'course-cash-used' | 'all-caught-up';

export type AttentionTaskStatus = 'pending' | 'in-progress' | 'completed';

export interface AttentionTask {
  id: string;
  kind: AttentionTaskKind;
  status: AttentionTaskStatus;
  /** ISO date string for the "due mm/dd" / "use by mm/dd" chip, when known. */
  dueDate?: string | null;
}

export interface AttentionAnchor {
  kind: AttentionAnchorKind;
  /** Approval total in cents, for the `approved` anchor's "Your approval total is $X". */
  approvedTotal?: number;
}

export interface AttentionModel {
  /** false → render no Needs Your Attention panel at all (the suppressed variant). */
  visible: boolean;
  anchors: AttentionAnchor[];
  /** All tasks incl. completed (the right-rail Tasks list); the NYA panel renders only the non-completed ones as cards. */
  tasks: AttentionTask[];
  /** Count of non-completed tasks → the "(N tasks pending)" header. */
  pendingCount: number;
}

/**
 * The slice of `TasApplicationExtension` the derivation reads. The real model
 * satisfies this structurally; tests pass plain stubs.
 */
export interface AttentionInput {
  state: string;
  amounts?: { course_cash?: { reserved?: number; used?: number } | null } | null;
  isEvidenceRequired: boolean;
  isMissingRequiredEvidence: boolean;
  isApproved: boolean;
  approvedTotal: number;
  /** Optional due/use-by dates + receipt signal (see TODO above). */
  courseCashUseByDate?: string | null;
  evidenceDueDate?: string | null;
  receiptDueDate?: string | null;
  receiptUploaded?: boolean;
}

// Just-submitted, awaiting first review → the "submitted and under review" anchor.
const SUBMITTED_REVIEW_STATES = new Set<string>([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

// Nothing left to do → the "you're all caught up" empty state.
const CAUGHT_UP_STATES = new Set<string>([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

export function deriveAttention(app: AttentionInput): AttentionModel {
  const tasks: AttentionTask[] = [];
  const anchors: AttentionAnchor[] = [];

  const reserved = app.amounts?.course_cash?.reserved ?? 0;
  const used = app.amounts?.course_cash?.used ?? 0;
  const hasCourseCash = reserved > 0;
  const courseCashUsed = hasCourseCash && used > 0;

  // Use CourseCash — present whenever a card is issued. Completes once spent; while
  // completed it stays in the rail list but is filtered out of the NYA cards.
  if (hasCourseCash) {
    tasks.push({
      id: 'use-course-cash',
      kind: 'use-course-cash',
      status: courseCashUsed ? 'completed' : 'pending',
      dueDate: app.courseCashUseByDate ?? null,
    });
  }

  // Upload CourseCash receipt — required once the card is used, until a receipt is in.
  if (courseCashUsed && !app.receiptUploaded) {
    tasks.push({
      id: 'upload-receipt',
      kind: 'upload-receipt',
      status: 'pending',
      dueDate: app.receiptDueDate ?? null,
    });
  }

  // Submit evidence — required by the program and not yet provided.
  if (app.isEvidenceRequired && app.isMissingRequiredEvidence) {
    tasks.push({
      id: 'submit-evidence',
      kind: 'submit-evidence',
      status: 'pending',
      dueDate: app.evidenceDueDate ?? null,
    });
  }

  // Informational anchors render above the action cards.
  if (app.isApproved) {
    anchors.push({ kind: 'approved', approvedTotal: app.approvedTotal });
  }
  if (courseCashUsed) {
    anchors.push({ kind: 'course-cash-used' });
  }

  const pendingCount = tasks.filter((task) => task.status !== 'completed').length;

  // Zero actionable + zero anchor → one of the three quiet variants.
  if (pendingCount === 0 && anchors.length === 0) {
    if (SUBMITTED_REVIEW_STATES.has(app.state)) {
      return { visible: true, anchors: [{ kind: 'submitted' }], tasks, pendingCount: 0 };
    }
    if (CAUGHT_UP_STATES.has(app.state)) {
      return { visible: true, anchors: [{ kind: 'all-caught-up' }], tasks, pendingCount: 0 };
    }
    // Under review with nothing to surface → suppress the panel entirely.
    return { visible: false, anchors: [], tasks, pendingCount: 0 };
  }

  return { visible: true, anchors, tasks, pendingCount };
}
